import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import type {
  DimensionInfo,
  JobNimbusInfo,
  EstimateType,
  PDFPreviewData,
  ScopeOfWorkData,
} from "@rebuild/shared";
import {
  extractScopeText,
  extractInitialSubtotal,
  hasBreakdownData as checkHasBreakdownData,
  isRoomGrouped as checkIsRoomGrouped,
  calculatePricing,
  calculateTaxRate,
  cleanCurrencyForApi,
  buildPricingAndDimensionInfo,
  formatTaxRateInput,
  getDefaultIncludeOverheadAndProfit,
  parseTaxRateInput,
} from "@rebuild/shared";
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { useGeneratePDFPreviewMutation, useExportPDFMutation, useZipTaxRateMutation } from "@/hooks/mutation/pdf-export";
import { PDFPreviewDocument } from "./PDFPreviewDocument";
import { PDFTaxControls } from "./PDFTaxControls";
import type { PDFPreviewFormData } from "./pdfPreview.types";

interface PDFPreviewModalProps {
  open: boolean;
  estimateId: string;
  projectName?: string;
  estimatePropertyAddress?: string;
  jobNimbusInfo?: JobNimbusInfo;
  dimensionsData: DimensionInfo[];
  isVerbose: boolean;
  isTimeAndMaterial?: boolean;
  /** Used to default "Include Overhead and Profit" (reconstruction vs custom O&P rates). */
  estimateType?: EstimateType;
  onComplete: () => void;
  onCancel: () => void;
}

export default function PDFPreviewModal({
  open,
  estimateId,
  projectName,
  estimatePropertyAddress,
  jobNimbusInfo,
  dimensionsData,
  isVerbose,
  isTimeAndMaterial = false,
  estimateType,
  onComplete,
  onCancel,
}: PDFPreviewModalProps) {
  const [originalScopeNoteText, setOriginalScopeNoteText] = useState("");
  const [editableScopeOfWork, setEditableScopeOfWork] = useState<ScopeOfWorkData | null>(null);
  const [sendToJobNimbus, setSendToJobNimbus] = useState(false);

  useEffect(() => {
    if (open) {
      setSendToJobNimbus(!!jobNimbusInfo);
    }
  }, [open, jobNimbusInfo]);

  const {
    mutateAsync: generatePDFPreview,
    isPending: isGeneratePending,
    data: previewData,
    variables: generateVariables,
    reset: resetGeneratePreview,
  } = useGeneratePDFPreviewMutation();

  const { mutateAsync: exportPDF, isPending: isExportPending } = useExportPDFMutation();
  const { mutateAsync: fetchZipTaxRate } = useZipTaxRateMutation();
  const [ziptaxSuggestionRate, setZiptaxSuggestionRate] = useState<number | undefined>(undefined);
  const [suggestionRateResolved, setSuggestionRateResolved] = useState(false);

  const isLoading = isGeneratePending && !generateVariables?.clearCache;
  const isReloading = isGeneratePending && generateVariables?.clearCache === true;

  const { control, setValue, reset, getValues, watch } = useForm<PDFPreviewFormData>({
    defaultValues: {
      title: "",
      headerInfo: [],
      scopeOfWorkTitle: "",
      organizationName: "",
      terms: "",
      footerLines: [],
      scopeNoteText: "",
      subtotal: "",
      includeOverheadAndProfit: false,
      includeTax: false,
      taxRate: "",
      includeQuantity: false,
    },
  });

  // Watch only what we need for calculations
  const subtotal = watch("subtotal");
  const includeOverheadAndProfit = watch("includeOverheadAndProfit");
  const includeTax = watch("includeTax");
  const formTaxRate = watch("taxRate");
  const includeQuantity = watch("includeQuantity");

  // Load preview when modal opens
  useEffect(() => {
    if (open) {
      resetGeneratePreview();
      generatePDFPreview({
        estimateId,
        pricingAndDimensionInfo: buildPricingAndDimensionInfo(isTimeAndMaterial, [], dimensionsData),
        verbose: isVerbose,
        clearCache: false,
      }).catch(() => {
        toast.error("Failed to load PDF preview");
        onCancel();
      });
    }
  }, [open, estimateId, dimensionsData, isVerbose, isTimeAndMaterial, generatePDFPreview, resetGeneratePreview, onCancel]);

  // Initialize form when preview data loads
  useEffect(() => {
    if (previewData) {
      const initialScopeText = extractScopeText(previewData.scopeOfWorkCategories);
      const initialSubtotal = extractInitialSubtotal(previewData);
      // Whether tax was included last export. Prefer the explicit flag; fall back to "a rate exists"
      // for previews persisted before the flag existed. The default is driven entirely by the cached
      // preview — checked when a tax was saved, unchecked otherwise — never by the ZipTax suggestion.
      const initialIncludeTax = previewData.taxIncluded ?? previewData.taxRate !== undefined;
      // Seed the editable rate from the persisted rate only when tax was on. When tax was off the field
      // stays empty; checking the box pre-fills the ZipTax suggestion via handleIncludeTaxChange.
      const initialRate = initialIncludeTax ? previewData.taxRate : undefined;

      setOriginalScopeNoteText(initialScopeText);
      setEditableScopeOfWork(previewData.scopeOfWorkCategories);

      reset({
        title: previewData.title || "",
        headerInfo: previewData.headerInfo || [],
        scopeOfWorkTitle: previewData.scopeOfWorkTitle || "",
        organizationName: previewData.organizationName || "",
        terms: previewData.terms || "",
        footerLines: previewData.footerLines || [],
        scopeNoteText: initialScopeText,
        subtotal: initialSubtotal,
        includeOverheadAndProfit: getDefaultIncludeOverheadAndProfit(estimateType, previewData),
        includeTax: initialIncludeTax,
        taxRate: initialRate !== undefined ? formatTaxRateInput(initialRate) : "",
        includeQuantity: false,
      });
    }
  }, [previewData, estimateType, reset]);

  // Every time the modal opens, fetch a fresh ZipTax suggestion for the estimate's current address from
  // the lightweight endpoint. We stay quiet (render no copy) until it resolves; the result only drives
  // the suggested-rate hint, never the default checkbox state.
  useEffect(() => {
    if (!open) {
      return;
    }
    setZiptaxSuggestionRate(undefined);
    setSuggestionRateResolved(false);
    const address = estimatePropertyAddress?.trim();
    if (!address) {
      // No address to look up: nothing is in flight, so the controls can show the "add an address" copy.
      setSuggestionRateResolved(true);
      return;
    }
    let cancelled = false;
    fetchZipTaxRate(address)
      .then((rate) => {
        if (!cancelled) setZiptaxSuggestionRate(rate);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSuggestionRateResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, estimatePropertyAddress, fetchZipTaxRate]);

  // Check if this is a reconstruction estimate (has breakdown)
  const hasBreakdown = checkHasBreakdownData(previewData ?? null);

  // Check if the preview is room-grouped (subtotal should not be editable for room-grouped)
  const isRoomGroupedPreview = checkIsRoomGrouped(previewData ?? null);
  const isSubtotalEditable = hasBreakdown && !isRoomGroupedPreview;
  const isTotalEditable = !hasBreakdown && !isRoomGroupedPreview;

  const parsedFormTaxRate = parseTaxRateInput(formTaxRate);
  const pricingPreviewData = includeTax && previewData ? { ...previewData, taxRate: parsedFormTaxRate } : (previewData ?? null);
  const pricing = calculatePricing(subtotal, pricingPreviewData, includeOverheadAndProfit, includeTax);

  const { overheadFormatted: overhead, profitFormatted: profit, taxAmount, total, overheadPercent, profitPercent } = pricing;
  const hasAddress = Boolean(estimatePropertyAddress?.trim());
  // While ZipTax is still in flight for a real address, stay quiet (render no copy) instead of flashing
  // "unable to calculate". Once it resolves we either show the suggested rate or the unable-to-calculate note.
  const isSuggestionLoading = hasAddress && !suggestionRateResolved;
  // Suggest "Apply suggested tax" only when tax is on and the rate on the PDF (derived from the user's
  // cash entry, persisted at two decimals) differs from the ZipTax suggestion. formatTaxRateInput rounds
  // both to two decimals first, so ZipTax's extra precision never produces a spurious suggestion.
  const isManualRate =
    includeTax && ziptaxSuggestionRate !== undefined && formatTaxRateInput(parsedFormTaxRate) !== formatTaxRateInput(ziptaxSuggestionRate);

  const handleIncludeTaxChange = (nextIncludeTax: boolean) => {
    setValue("includeTax", nextIncludeTax);

    if (!nextIncludeTax) {
      return;
    }

    const nextTaxRate = parseTaxRateInput(getValues("taxRate")) || ziptaxSuggestionRate || calculateTaxRate(previewData ?? null);
    setValue("taxRate", formatTaxRateInput(nextTaxRate));
  };

  // "Use suggested tax": swap a manual rate for the current ZipTax suggestion, keeping tax included.
  const handleResetToSuggestion = () => {
    if (ziptaxSuggestionRate === undefined) {
      return;
    }

    setValue("taxRate", formatTaxRateInput(ziptaxSuggestionRate));
  };

  const handleExport = async () => {
    if (!previewData) return;

    const formData = getValues();

    // Clean currency values for API
    const cleanSubtotal = cleanCurrencyForApi(formData.subtotal);
    const cleanTotal = cleanCurrencyForApi(total);
    const cleanTaxAmount = cleanCurrencyForApi(taxAmount);
    // Always persist `taxIncluded` as an explicit boolean. The cache is saved with a Firestore merge,
    // which can't delete an omitted field — so a real `false` is what reliably overwrites a previously
    // saved `true` when the user turns tax off. Rate/amount are only persisted when tax is on.
    const taxPreviewFields = formData.includeTax
      ? {
          taxIncluded: true,
          taxRate: parseTaxRateInput(formData.taxRate),
          taxAmount: cleanTaxAmount,
        }
      : { taxIncluded: false };

    // Prepare scopeOfWorkCategories with textOverride if text was edited and it's category-grouped
    let finalScopeOfWorkCategories = editableScopeOfWork || previewData.scopeOfWorkCategories;

    // If text was changed and it's category-grouped, add textOverride
    if (
      !isRoomGroupedPreview &&
      formData.scopeNoteText !== originalScopeNoteText &&
      finalScopeOfWorkCategories.type === "category-grouped"
    ) {
      finalScopeOfWorkCategories = {
        ...finalScopeOfWorkCategories,
        textOverride: formData.scopeNoteText,
      };
    }

    // Construct the updated PDFPreviewData object
    const updatedPreviewData: PDFPreviewData = {
      title: formData.title,
      bannerImageUrl: previewData.bannerImageUrl,
      bannerImageScale: previewData.bannerImageScale,
      bannerImageAlignment: previewData.bannerImageAlignment,
      footerImageUrl: previewData.footerImageUrl,
      headerInfo: formData.headerInfo,
      organizationName: formData.organizationName,
      scopeOfWorkTitle: formData.scopeOfWorkTitle,
      scopeOfWorkCategories: finalScopeOfWorkCategories,
      totalCostEstimate: cleanTotal,
      subtotal: cleanSubtotal,
      preTaxTotal: pricing.preTaxTotal,
      overhead: hasBreakdown && formData.includeOverheadAndProfit ? overhead : undefined,
      profit: hasBreakdown && formData.includeOverheadAndProfit ? profit : undefined,
      ...taxPreviewFields,
      terms: formData.terms,
      footerLines: formData.footerLines,
      topLeftText: previewData.topLeftText,
      includeQuantity: formData.includeQuantity,
    };

    try {
      const filename = projectName ? `${projectName} - Estimate Proposal.pdf` : "Estimate Proposal.pdf";
      const pdfBytes = await exportPDF({
        estimateId,
        pdfPreviewData: updatedPreviewData,
        filename,
        sendToJobNimbus: sendToJobNimbus && !!jobNimbusInfo,
      });

      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("PDF export completed");
      onComplete();
    } catch {
      toast.error("Failed to export PDF");
    }
  };

  const handleReset = async () => {
    try {
      await generatePDFPreview({
        estimateId,
        pricingAndDimensionInfo: buildPricingAndDimensionInfo(isTimeAndMaterial, [], dimensionsData),
        verbose: isVerbose,
        clearCache: true,
      });
      toast.success("PDF preview reset");
    } catch {
      toast.error("Failed to reset PDF preview");
    }
  };

  if (isLoading || !previewData) {
    return (
      <Dialog open={open} onOpenChange={onCancel}>
        <DialogContent className="flex max-h-[90%] w-full max-w-7xl flex-col rounded-lg bg-white! p-6 shadow-lg">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-600">Loading preview...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="flex max-h-[90%] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white! p-6 shadow-lg">
        <div className="flex w-full flex-1 flex-row gap-6 overflow-hidden">
          {/* Left side - PDF Preview */}
          <PDFPreviewDocument
            previewData={previewData}
            control={control}
            setValue={setValue}
            editableScopeOfWork={editableScopeOfWork}
            setEditableScopeOfWork={setEditableScopeOfWork}
            overhead={overhead}
            profit={profit}
            taxAmount={taxAmount}
            total={total}
            isSubtotalEditable={isSubtotalEditable}
            isTotalEditable={isTotalEditable}
            includeTax={includeTax}
            includeOverheadAndProfit={includeOverheadAndProfit}
            overheadPercent={overheadPercent}
            profitPercent={profitPercent}
            isReloading={isReloading}
            includeQuantity={includeQuantity}
          />

          {/* Right side - Controls */}
          <div className="flex flex-1 flex-col overflow-y-auto">
            {/* Title and Directive */}
            <div className="mb-10">
              <h2 className="mb-2 text-2xl leading-7 font-semibold">PDF Preview & Export</h2>
              <p className="text-sm text-gray-600">Click on text in the PDF Preview to edit it</p>
            </div>

            {/* Options Section (tax controls always render; the sales-tax option is always offered) */}
            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold">Options</h3>
              <div className="space-y-3">
                {isVerbose && (
                  <Controller
                    name="includeQuantity"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox checked={value} onCheckedChange={() => onChange(!value)} />
                        <label className="cursor-pointer text-sm text-gray-700" onClick={() => onChange(!value)}>
                          Include Line Item Quantity
                        </label>
                      </div>
                    )}
                  />
                )}
                {hasBreakdown && (
                  <Controller
                    name="includeOverheadAndProfit"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox checked={value} onCheckedChange={() => onChange(!value)} />
                        <label className="cursor-pointer text-sm text-gray-700" onClick={() => onChange(!value)}>
                          Include Overhead and Profit ({overheadPercent}% overhead, {profitPercent}% profit)
                        </label>
                      </div>
                    )}
                  />
                )}
                <PDFTaxControls
                  estimatePropertyAddress={estimatePropertyAddress}
                  hasAddress={hasAddress}
                  includeTax={includeTax}
                  ziptaxRate={ziptaxSuggestionRate}
                  isManualRate={isManualRate}
                  isSuggestionLoading={isSuggestionLoading}
                  onIncludeTaxChange={handleIncludeTaxChange}
                  onResetToSuggestion={handleResetToSuggestion}
                />

                {jobNimbusInfo && (
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={sendToJobNimbus} onCheckedChange={() => setSendToJobNimbus((v) => !v)} />
                    <label className="cursor-pointer text-sm text-gray-700" onClick={() => setSendToJobNimbus((v) => !v)}>
                      Send PDF to JobNimbus
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* Action Buttons */}
            <div className="border-accent-foreground flex flex-col gap-3 border-t pt-4">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>

              <Button variant="outline" onClick={handleReset}>
                Reset to original
              </Button>

              <Button variant="default" onClick={handleExport} disabled={isExportPending}>
                {isExportPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting PDF...
                  </>
                ) : (
                  "Save & Export PDF"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
